// ==== src/components/data-sources/hooks/useFileSelection.tsx ====

import { useState, useCallback } from 'react';
import { ListItem } from '../../../types/files';

export const useFileSelection = () => {
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

    const handleSelect = useCallback((
        itemId: string,
        event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
        sortedItems: ListItem[]
    ) => {
        event.stopPropagation();

        const isMouseEvent = (e: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>):
            e is React.MouseEvent<HTMLElement> => {
            return 'button' in e;
        };

        setSelectedFiles(prev => {
            const newSelection = new Set(prev);

            if (isMouseEvent(event) && (event.metaKey || event.ctrlKey)) {
                if (newSelection.has(itemId)) {
                    newSelection.delete(itemId);
                } else {
                    newSelection.add(itemId);
                }
            } else if (isMouseEvent(event) && event.shiftKey && lastSelectedId) {
                const items = sortedItems.map(item => item.id);
                const currentIndex = items.indexOf(itemId);
                const lastIndex = items.indexOf(lastSelectedId);

                const start = Math.min(currentIndex, lastIndex);
                const end = Math.max(currentIndex, lastIndex);

                items.slice(start, end + 1).forEach(id => {
                    newSelection.add(id);
                });
            } else if (isMouseEvent(event) && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
                newSelection.clear();
                newSelection.add(itemId);
            } else {
                if (newSelection.has(itemId)) {
                    newSelection.delete(itemId);
                } else {
                    newSelection.add(itemId);
                }
            }

            return newSelection;
        });

        setLastSelectedId(itemId);
    }, [lastSelectedId]);

    const handleSelectAll = useCallback((sortedItems: ListItem[]) => {
        const allIds = sortedItems.map(item => item.id);
        setSelectedFiles(prev =>
            prev.size === allIds.length ? new Set() : new Set(allIds)
        );
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedFiles(new Set());
        setLastSelectedId(null);
    }, []);

    return {
        selectedFiles,
        lastSelectedId,
        handleSelect,
        handleSelectAll,
        clearSelection
    };
};